/**
 * E024-S001-T003: CSV Import Preview Component
 * E024-S001-T006: DocSelector Integration
 *
 * Shows preview of CSV files before import with configuration options.
 */

import { Button, Switch, Tooltip } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';

import { type BasePreviewProps } from '../../../../modules/streamlain/file-import';
import {
  DatabaseImportParserWorkerUnavailableError,
  preflightCsvFileInWorker,
} from '../../../../modules/streamlain/file-import/database-import-parser-worker-client';
import type { DatabaseImportParserProgress } from '../../../../modules/streamlain/file-import/database-import-parser-worker-types';
import { DocSelector } from '../../../../modules/streamlain/sidebar/components';
import { ImportAnalysisLoading } from '../analysis-loading';
import {
  formatImportCount,
  formatImportFilesSelected,
  formatImportMoreRows,
} from '../preview-i18n';
import * as styles from './styles.css';
import type { CsvImportConfig, CsvPreviewData } from './types';

interface CsvImportPreviewProps extends BasePreviewProps<CsvImportConfig> {
  /** E024-S001-T005: Target doc ID for drag & drop (makes selector read-only) */
  targetDocId?: string;
  /** E024-S001-T005: Target doc name for display */
  targetDocName?: string;
  useDatabaseImportJob?: boolean;
}

export function CsvImportPreview({
  files,
  onImport,
  onCancel,
  importing = false,
  targetDocId,
  targetDocName,
  useDatabaseImportJob = false,
}: CsvImportPreviewProps) {
  const t = useI18n();
  const tRef = useRef(t);
  const [previews, setPreviews] = useState<CsvPreviewData[]>([]);
  const [docName, setDocName] = useState(
    files.length === 1
      ? files[0].name.replace(/\.csv$/i, '')
      : t['com.streamlain.import.preview.csv.default-document-name']()
  );
  const [existingDocId, setExistingDocId] = useState<string | undefined>(
    targetDocId
  );
  const [hasHeader, setHasHeader] = useState(true);
  const [selectedFile, setSelectedFile] = useState(0);
  const [titleColumnIndex, setTitleColumnIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [analysisProgress, setAnalysisProgress] =
    useState<DatabaseImportParserProgress | null>(null);

  // E024-S001-T005: Check if this is a drag & drop import (read-only mode)
  const isReadOnly = !!targetDocId;

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  // Preflight files before any full extraction or document mutation.
  useEffect(() => {
    const controller = new AbortController();

    async function parseFiles() {
      setLoading(true);
      setLoadError(null);
      setAnalysisProgress(null);
      const results: CsvPreviewData[] = [];

      for (const file of files) {
        const preflight = await preflightCsvFileInWorker(
          { file, hasHeader },
          controller.signal,
          progress => {
            if (!controller.signal.aborted) {
              setAnalysisProgress(progress);
            }
          }
        );
        if (controller.signal.aborted) return;

        results.push({
          fileName: file.name,
          columns: preflight.columns.map(column => ({
            name: column.name,
            type: column.type,
          })),
          previewRows: preflight.previewRows,
          totalRows: preflight.estimate.rowCount,
          preflight,
        });
      }

      if (controller.signal.aborted) return;
      setPreviews(results);
      setTitleColumnIndex(0); // Reset title column when files change
      setLoading(false);
    }

    parseFiles().catch(error => {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error(error);
      if (!controller.signal.aborted) {
        const currentT = tRef.current;
        setLoadError(
          error instanceof DatabaseImportParserWorkerUnavailableError
            ? currentT[
                'com.streamlain.import.status.failed.message.parser-unavailable'
              ]()
            : currentT['com.streamlain.import.status.failed.message']()
        );
        setLoading(false);
      }
    });

    return () => {
      controller.abort();
    };
  }, [files, hasHeader]);

  const handleImport = useCallback(() => {
    onImport({
      files,
      newDocName: docName,
      hasHeader,
      titleColumnIndex,
      estimatedTotalRows: previews.reduce(
        (sum, preview) => sum + preview.totalRows,
        0
      ),
      existingDocId,
    });
  }, [
    files,
    docName,
    hasHeader,
    previews,
    titleColumnIndex,
    existingDocId,
    onImport,
  ]);

  // E024-S001-T006: Handle doc selection change
  const handleDocChange = useCallback(
    (value: string, isExisting: boolean, docId?: string) => {
      setDocName(value);
      setExistingDocId(isExisting ? docId : undefined);
    },
    []
  );

  const currentPreview = previews[selectedFile];
  const tooLargePreviews = previews.filter(preview =>
    preview.preflight.estimate.allowedAction.startsWith('blocked-')
  );
  const canImportDirect =
    !useDatabaseImportJob && tooLargePreviews.length === 0;
  const canImportJob = useDatabaseImportJob && tooLargePreviews.length === 0;
  const canImport = canImportDirect || canImportJob;

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KiB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
  };
  const totalRows = previews.reduce(
    (sum, preview) => sum + preview.totalRows,
    0
  );
  const totalFileBytes = files.reduce((sum, file) => sum + file.size, 0);

  if (loading) {
    return (
      <div className={styles.container}>
        <ImportAnalysisLoading
          title={t['com.streamlain.import.preview.csv.analysis.title']()}
          progress={analysisProgress}
          onCancel={onCancel}
        />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>{loadError}</div>
        <div className={styles.footer}>
          <Button variant="secondary" onClick={onCancel}>
            {t['Cancel']()}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <div className={styles.title}>
            {t['com.streamlain.import.preview.csv.title']()}
          </div>
          {currentPreview && (
            <div className={styles.preflightSummary}>
              <span className={styles.preflightMetric}>
                {formatImportCount(t, 'file', files.length)}
              </span>
              <span className={styles.preflightMetric}>
                {formatImportCount(t, 'row', totalRows)}
              </span>
              <span className={styles.preflightMetric}>
                {formatImportCount(t, 'column', currentPreview.columns.length)}
              </span>
              <span className={styles.preflightMetric}>
                {formatBytes(totalFileBytes)}
              </span>
            </div>
          )}
        </div>
        <div className={styles.subtitle}>
          {formatImportFilesSelected(t, files.length)}
        </div>
      </div>

      <div className={styles.content}>
        {/* File List */}
        {files.length > 1 && (
          <div className={styles.fileList}>
            {files.map((file, index) => (
              <div
                key={file.name}
                className={styles.fileItem}
                data-selected={index === selectedFile}
                onClick={() => setSelectedFile(index)}
              >
                <span>{file.name}</span>
                <span className={styles.rowCount}>
                  {previews[index]
                    ? formatImportCount(t, 'row', previews[index].totalRows)
                    : t['com.streamlain.import.preview.loading-placeholder']()}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Preview Table */}
        {currentPreview && (
          <div className={styles.previewTable}>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {currentPreview.columns.map((col, i) => (
                      <Tooltip
                        key={i}
                        content={
                          i === titleColumnIndex
                            ? t[
                                'com.streamlain.import.preview.title-column.tooltip.current'
                              ]()
                            : t[
                                'com.streamlain.import.preview.title-column.tooltip.set'
                              ]()
                        }
                      >
                        <th
                          className={styles.th}
                          data-title-column={i === titleColumnIndex}
                          onClick={() => setTitleColumnIndex(i)}
                        >
                          {col.name}
                          {i === titleColumnIndex && (
                            <span className={styles.titleBadge}>
                              {t[
                                'com.streamlain.import.preview.title-column'
                              ]()}
                            </span>
                          )}
                        </th>
                      </Tooltip>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentPreview.previewRows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          className={styles.td}
                          data-title-column={cellIndex === titleColumnIndex}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {currentPreview.totalRows > 10 && (
              <div className={styles.moreRows}>
                {formatImportMoreRows(t, currentPreview.totalRows - 10)}
              </div>
            )}
          </div>
        )}

        {(useDatabaseImportJob || !canImportDirect) && (
          <div className={styles.blocked}>
            {tooLargePreviews.length > 0
              ? t['com.streamlain.import.preview.csv.blocked.too-large']()
              : useDatabaseImportJob
                ? existingDocId
                  ? t[
                      'com.streamlain.import.preview.csv.server-job.existing-doc'
                    ]()
                  : t['com.streamlain.import.preview.csv.server-job.new-doc']()
                : t[
                    'com.streamlain.import.preview.csv.blocked.not-importable'
                  ]()}
          </div>
        )}

        {/* Options */}
        <div className={styles.options}>
          <div className={styles.option}>
            <label className={styles.optionLabel}>
              {t['com.streamlain.import.preview.document']()}
            </label>
            <DocSelector
              value={docName}
              onChange={handleDocChange}
              placeholder={t[
                'com.streamlain.import.preview.document.placeholder'
              ]()}
              readOnly={isReadOnly}
              readOnlyLabel={targetDocName || docName}
            />
          </div>

          <div className={styles.option}>
            <label className={styles.optionLabel}>
              {t['com.streamlain.import.preview.first-row-header']()}
            </label>
            <Switch checked={hasHeader} onChange={setHasHeader} />
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <Button variant="secondary" onClick={onCancel} disabled={importing}>
          {t['Cancel']()}
        </Button>
        <Button
          variant="primary"
          onClick={handleImport}
          disabled={importing || !canImport}
        >
          {importing
            ? t['com.streamlain.import.preview.action.importing']()
            : canImportJob
              ? t['com.streamlain.import.preview.action.start-jobs']({
                  count: String(files.length),
                })
              : t['com.streamlain.import.preview.csv.action.import-files']({
                  count: String(files.length),
                })}
        </Button>
      </div>
    </div>
  );
}
